import { useState, useRef, useEffect, useCallback } from 'react';

import type { RefObject, Dispatch, SetStateAction } from 'react';

/**
 * @description
 * 使用 useRef 和 useState，创建一个可变的 ref 对象和一个状态值。
 * 引用对象始终反映最新的状态值。
 * @param T - 状态值的泛型类型
 * @param {T} initialValue - 状态的初始值。
 * @returns {[T, RefObject<T>, Dispatch<SetStateAction<T>>]} - 包含状态值、引用对象和状态设置函数的元组。
 */
export function useRefState<T>(initialValue: T): [T, RefObject<T>, Dispatch<SetStateAction<T>>] {
    const [state, setState] = useState(initialValue);
    const stateRef = useRef(state);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    const innerSetState = useCallback((v: T | ((ps: T) => T)) => {
        if (typeof v !== 'function') {
            stateRef.current = v;
        }
        setState(v);
    }, []);

    return [state, stateRef, innerSetState];
}
